import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const services = [
  "AI Strategy & Executive Advisory",
  "Custom AI Implementation",
  "Custom Development",
  "Digital Transformation",
  "High-ROI Marketing",
  "Something else",
];

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-base text-[#0F172A] placeholder:text-slate-400 outline-none transition-all focus:border-[#0FA89E] focus:ring-4 focus:ring-[rgba(0,82,255,0.1)]";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: services[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail?.[0]?.msg || data.detail || "Submission failed");
      }
      toast.success("Inquiry received — we will reply within one business day.");
      setForm({ name: "", email: "", company: "", service: services[0], message: "" });
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 lg:py-32 bg-[#FAFAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-4">
              Contact
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] mb-5">
              Let's build the tool you actually need
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-10">
              Tell us what you're actually trying to do — not what the software
              vendor told you to do. A senior consultant — not a sales rep —
              replies within one business day.
            </p>
            <div className="space-y-5">
              <div>
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-1">
                  Email
                </p>
                <a
                  href="mailto:hello@mayforward.com"
                  data-testid="contact-email-link"
                  className="text-base font-semibold text-[#0F172A] hover:text-[#0FA89E] transition-colors"
                >
                  hello@mayforward.com
                </a>
              </div>
              <div>
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-1">
                  Availability
                </p>
                <p className="text-base font-semibold text-[#0F172A]">
                  Fully booked for 2026
                </p>
                <p className="text-base text-[#0FA89E] font-semibold mt-1">
                  Accepting 2027 engagements
                </p>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-2xl border border-slate-200 bg-white p-7 sm:p-10 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.12)]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-input-name"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Work email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="jane@company.com"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
              <div>
                <label htmlFor="contact-company" className="block text-sm font-semibold text-slate-700 mb-2">
                  Company
                </label>
                <input
                  id="contact-company"
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Acme Corp"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-service" className="block text-sm font-semibold text-slate-700 mb-2">
                  Service of interest
                </label>
                <select
                  id="contact-service"
                  data-testid="contact-select-service"
                  value={form.service}
                  onChange={update("service")}
                  className={inputClass}
                >
                  {services.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mb-7">
              <label htmlFor="contact-message" className="block text-sm font-semibold text-slate-700 mb-2">
                What are you trying to achieve?
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-input-message"
                required
                minLength={10}
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about your goals, systems and timeline..."
                className={`${inputClass} resize-none`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              className="group inline-flex items-center justify-center gap-2.5 w-full sm:w-auto rounded-full bg-[#0FA89E] px-8 py-4 text-base font-semibold text-white shadow-[0_12px_35px_-10px_rgba(0,82,255,0.55)] transition-all hover:bg-[#0C8E85] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4.5 h-4.5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Build my tool
                  <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
